import { Icon, ICONS } from 'components/Icon';
import { Txt } from 'components/Txt';
import { theme } from 'lib/theme';
import { isEqual } from 'lodash';
import React from 'react';
import {
  PanResponder,
  PanResponderInstance,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { PlanLoaded } from 'store/plan/types';
import { CanvasImage } from './CanvasImage';
import { ViewSetTopLeft, ViewSetTopLeftRef } from './ViewSetTopLeft';

export const PIN_DIM = { height: 52, width: 42 };

const LOUPE_SIZE = 100;
const LOUPE_SIZE_HALF = 50;

const getHappyPosition = ({
  maxX,
  maxY,
  movedX,
  movedY,
  startX,
  startY,
}: {
  startX: number;
  startY: number;
  movedX: number;
  movedY: number;
  maxX: number;
  maxY: number;
}): { x: number; y: number } => {
  let left = startX + movedX;
  let top = startY + movedY;
  if (left < 0) {
    left = 0;
  } else if (left > maxX) {
    left = maxX;
  }
  if (top < 0) {
    top = 0;
  } else if (top > maxY) {
    top = maxY;
  }
  return { x: left, y: top };
};

type Props = {
  text?: string;
  onPress?: (id: string) => void;
  onPressUp?: (id: string) => void;
  onMoved?: ({ x, y }: { x: number; y: number }) => void;
  canvasWidth: number;
  canvasHeight: number;
  id: string;
  x: number;
  y: number;
  style?: StyleProp<ViewStyle>;
  active: boolean;
  color?: string;
  image?: PlanLoaded['image'];
  canHaveLoupe?: boolean;
};

const Ring = ({ duration, delay }: { duration: number; delay: number }) => {
  const ring = useSharedValue(0);

  const ringStyle = useAnimatedStyle(() => {
    return {
      opacity: 0.8 - ring.value,
      transform: [
        {
          scale: interpolate(ring.value, [0, 1], [0, 3]),
        },
      ],
    };
  });
  React.useEffect(() => {
    ring.value = withDelay(
      delay,
      withRepeat(
        withTiming(1, {
          duration,
        }),
        -1,
        false,
      ),
    );
  }, [delay, duration, ring]);
  return <Animated.View style={[styles.ring, ringStyle]} />;
};

export const Pin: React.FC<Props> = React.memo(
  ({
    active,
    canvasHeight,
    canvasWidth,
    id,
    onMoved,
    onPress,
    onPressUp,
    style,
    text,
    x,
    y,
    color,
    image,
    canHaveLoupe,
  }) => {
    const pinRef = React.useRef<ViewSetTopLeftRef>(null);
    const loopPreviewRef = React.useRef<ViewSetTopLeftRef>(null);
    const [loupeVisible, setLoupeVisible] = React.useState(false);
    const loupeScale = 2;

    const { finalX, finalY } = React.useMemo(
      () => ({
        finalX: (canvasWidth * x) / 100,
        finalY: (canvasHeight * y) / 100,
      }),
      [canvasHeight, canvasWidth, x, y],
    );

    const handleMoved = React.useCallback(
      (movedX: number, movedY: number) => {
        setLoupeVisible(false);
        if (movedX || movedY) {
          const { x: newX, y: newY } = getHappyPosition({
            maxX: canvasWidth,
            maxY: canvasHeight,
            movedX,
            movedY,
            startX: finalX,
            startY: finalY,
          });
          onMoved?.({
            x: (newX / canvasWidth) * 100,
            y: (newY / canvasHeight) * 100,
          });
        }
      },
      [canvasHeight, canvasWidth, onMoved, finalX, finalY],
    );

    const handlePress = React.useCallback(() => {
      setLoupeVisible(true);
      onPress?.(id);
    }, [id, onPress]);

    const handlePressUp = React.useCallback(() => {
      onPressUp?.(id);
    }, [id, onPressUp]);

    const panResponder = React.useMemo((): null | PanResponderInstance => {
      return !onMoved
        ? null
        : PanResponder.create({
            onMoveShouldSetPanResponder: () => true,
            onMoveShouldSetPanResponderCapture: () => true,
            onPanResponderStart: () => handlePress(),
            onPanResponderMove: (_, gestureState) => {
              const { x: newX, y: newY } = getHappyPosition({
                maxX: canvasWidth,
                maxY: canvasHeight,
                movedX: gestureState.dx,
                movedY: gestureState.dy,
                startX: finalX,
                startY: finalY,
              });
              pinRef.current?.setTopLeft({
                left: newX,
                top: newY,
              });
              loopPreviewRef.current?.setTopLeft({
                left: -(newX * loupeScale - LOUPE_SIZE_HALF),
                top: -(newY * loupeScale - LOUPE_SIZE_HALF),
              });
            },
            onPanResponderRelease: (_, gestureState) => {
              handleMoved(gestureState.dx, gestureState.dy);
              handlePressUp();
            },
            onPanResponderTerminate: (_, gestureState) => {
              handleMoved(gestureState.dx, gestureState.dy);
              handlePressUp();
            },
            onPanResponderTerminationRequest: () => true,
            onStartShouldSetPanResponder: () => true,
            onStartShouldSetPanResponderCapture: () => true,
          });
    }, [
      onMoved,
      handlePress,
      canvasWidth,
      canvasHeight,
      finalX,
      finalY,
      pinRef,
      loopPreviewRef,
      loupeScale,
      handleMoved,
      handlePressUp,
    ]);

    const PinView = React.useMemo(() => {
      const textWidth = 38;
      const textHeight = 12;
      const textLength = text?.toString().length || 1;
      const fontSize = Math.min(
        Math.sqrt((textWidth * textHeight) / textLength),
        20,
      );
      return (
        <View
          style={{
            alignItems: 'center',
            height: PIN_DIM.height,
            width: PIN_DIM.width,
            justifyContent: 'flex-start',
            overflow: 'visible',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          <Icon
            source={ICONS.LocationPinSolidStroke}
            size={PIN_DIM.height}
            color={active ? theme.colors.green : color || theme.colors.black}
            style={styles.icon}
          />
          {!!text && (
            <View style={styles.numberContainer}>
              <Txt
                weight="bold"
                lineHeightNone
                style={[
                  styles.number,
                  {
                    fontSize,
                  },
                ]}
              >
                {text}
              </Txt>
            </View>
          )}
        </View>
      );
    }, [text, active, color]);

    return panResponder ? (
      <ViewSetTopLeft
        ref={pinRef}
        style={[
          /** @ts-expect-error react-native-web has cursor */
          Platform.select({ web: { cursor: 'move' } }),
          styles.view,
          active ? styles.viewActive : undefined,
          style,
          {
            transform: [
              { translateY: PIN_DIM.height / 2 },
              { translateY: -(PIN_DIM.height / 2) },
            ],
          },
        ]}
        initTopLeft={{ left: finalX, top: finalY }}
        {...panResponder.panHandlers}
      >
        {PinView}
        <Ring delay={0} duration={3000} />
        <Ring delay={500} duration={3000} />
        {canHaveLoupe && image && (
          <View
            style={[styles.loupe, loupeVisible ? undefined : { left: -99999 }]}
          >
            <View style={styles.loupeCircle}>
              <View style={styles.loupeTarget} />
              <ViewSetTopLeft
                ref={loopPreviewRef}
                style={[
                  styles.loupeContainer,
                  {
                    width: canvasWidth * loupeScale,
                    height: canvasHeight * loupeScale,
                  },
                ]}
                initTopLeft={{
                  left: -(finalX * loupeScale - LOUPE_SIZE_HALF),
                  top: -(finalY * loupeScale - LOUPE_SIZE_HALF),
                }}
              >
                <CanvasImage
                  key={`${canvasWidth}x${canvasHeight}`}
                  base64={image.base64}
                  url={image.url}
                  width={canvasWidth * loupeScale}
                  height={canvasHeight * loupeScale}
                  page={image.page}
                  type={image.type}
                />
              </ViewSetTopLeft>
            </View>
          </View>
        )}
      </ViewSetTopLeft>
    ) : (
      <View
        onTouchStart={Platform.OS !== 'web' ? handlePress : undefined}
        // @ts-expect-error onMouseDown exists in react-native-web
        onMouseDown={Platform.OS === 'web' ? handlePress : undefined}
        style={[
          onPress
            ? Platform.select({
                web: { cursor: 'pointer' },
              })
            : undefined,
          styles.view,
          active ? styles.viewActive : undefined,
          !active && !onPress ? styles.viewDisabled : undefined,
          style,
          {
            left: finalX,
            top: finalY,
            transform: [
              { translateY: PIN_DIM.height / 2 },
              { translateY: -(PIN_DIM.height / 2) },
            ],
          },
        ]}
      >
        {PinView}
      </View>
    );
  },
  (a, b) => isEqual(a, b),
);

const styles = StyleSheet.create({
  icon: {
    overflow: 'visible',
    shadowColor: 'black',
    shadowOffset: { height: 0, width: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  number: {
    color: 'white',
    textAlign: 'center',
    height: 30,
    lineHeight: 30,
  },
  numberContainer: {
    left: 0,
    right: 0,
    position: 'absolute',
    top: 5,
    zIndex: 2,
  },
  view: {
    height: PIN_DIM.height,
    marginLeft: -PIN_DIM.width / 2,
    marginTop: -PIN_DIM.height,
    opacity: 0.8,
    position: 'absolute',
    width: PIN_DIM.width,
    zIndex: 10,
  },
  viewActive: {
    opacity: 1,
    zIndex: 20,
  },
  viewDisabled: {
    opacity: 0.46,
    pointerEvents: 'none',
  },
  loupe: {
    borderColor: 'white',
    borderWidth: 1,
    position: 'absolute',
    left: -LOUPE_SIZE_HALF + PIN_DIM.width / 2,
    top: -90,
    zIndex: 999,
    width: LOUPE_SIZE + 2,
    height: LOUPE_SIZE + 2,
    overflow: 'visible',
    borderRadius: LOUPE_SIZE_HALF + 1,
    pointerEvents: 'none',
    shadowColor: 'black',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.6,
    shadowRadius: 5,
  },
  loupeContainer: {
    position: 'absolute',
    zIndex: 1,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loupeCircle: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: LOUPE_SIZE_HALF,
    width: LOUPE_SIZE,
    height: LOUPE_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loupeTarget: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: 'black',
    alignSelf: 'center',
    shadowColor: 'white',
    shadowOpacity: 1,
    shadowOffset: { width: 0, height: 0 },
    shadowRadius: 1,
    zIndex: 2,
  },
  ring: {
    position: 'absolute',
    marginLeft: (PIN_DIM.width - PIN_DIM.height) / 2,
    marginTop: PIN_DIM.height / 2,
    width: PIN_DIM.height,
    height: PIN_DIM.height,
    borderRadius: PIN_DIM.height / 2,
    backgroundColor: theme.colors.green,
    zIndex: 1,
    pointerEvents: 'none',
  },
});
